// In PrerequisitesEditor.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Space,
  List,
  Tag,
  Alert,
  Spin
} from "antd";
import {
  CheckSquareOutlined,
  SaveOutlined,
  PlusOutlined,
  DeleteOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { toast } from 'react-toastify';
import { updateCoursePrerequisites } from "../Services/CourseListService";

const { Text } = Typography;
const { TextArea } = Input;

const PrerequisitesEditor = ({ courseId, initialPrerequisites, onSave, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [prerequisites, setPrerequisites] = useState([]);
  const [currentPrerequisite, setCurrentPrerequisite] = useState("");

  // Initialize prerequisites from props - handle both string and object formats
  useEffect(() => {
    if (initialPrerequisites && initialPrerequisites.length > 0) {
      // Convert to array of strings regardless of input format
      const prereqStrings = initialPrerequisites.map(p => {
        if (typeof p === 'string') return p;
        if (p.description) return p.description;
        return String(p);
      });
      setPrerequisites(prereqStrings);
    }
  }, [initialPrerequisites]);

  const handleAddPrerequisite = () => {
    if (currentPrerequisite.trim() === "") {
      toast.error("Please enter a prerequisite");
      return;
    }

    if (prerequisites.includes(currentPrerequisite.trim())) {
      toast.warning("This prerequisite already exists");
      return;
    }

    setPrerequisites(prev => [...prev, currentPrerequisite.trim()]);
    setCurrentPrerequisite("");
    toast.success("Prerequisite added successfully!");
  };

  const handleRemovePrerequisite = (index) => {
    setPrerequisites(prev => prev.filter((_, i) => i !== index));
    toast.info("Prerequisite removed");
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddPrerequisite();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Send array of strings directly
      await updateCoursePrerequisites(courseId, prerequisites, false);
      toast.success("Prerequisites updated successfully!");
      onSave(prerequisites);
    } catch (error) {
      toast.error("Failed to update prerequisites: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title={
        <Space>
          <CheckSquareOutlined />
          <span>Edit Prerequisites</span>
        </Space>
      }
      extra={
        <Button
          type="text"
          icon={<CloseOutlined />}
          onClick={onCancel}
        />
      }
    >
      <Space direction="vertical" style={{ width: "100%" }} size="middle">
        <Text type="secondary">
          Add skills or knowledge required before taking this course
        </Text>

        {/* Add Prerequisite Input */}
        <Space.Compact style={{ width: "100%" }}>
          <Input
            placeholder="Enter prerequisite (e.g., 'Basic Python knowledge', 'Understanding of algebra')"
            value={currentPrerequisite}
            onChange={(e) => setCurrentPrerequisite(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPrerequisite}
          >
            Add
          </Button>
        </Space.Compact>

        {/* Prerequisites List */}
        {prerequisites.length > 0 ? (
          <Card size="small" title={`Prerequisites (${prerequisites.length})`}>
            <List
              size="small"
              dataSource={prerequisites}
              renderItem={(prerequisite, index) => (
                <List.Item
                  key={index}
                  actions={[
                    <Button
                      key="delete"
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemovePrerequisite(index)}
                    />
                  ]}
                >
                  <Space>
                    <CheckSquareOutlined style={{ color: '#52c41a' }} />
                    <Text>{prerequisite}</Text>
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        ) : (
          <Card size="small">
            <div style={{ textAlign: 'center', padding: '16px' }}>
              <Text type="secondary">No prerequisites added yet</Text>
            </div>
          </Card>
        )}

        {/* Form Actions */}
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            size="large"
            icon={<SaveOutlined />}
            onClick={handleSubmit}
          >
            Save Prerequisites
          </Button>
          <Button
            onClick={onCancel}
            size="large"
          >
            Cancel
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

// Add prop type definitions
PrerequisitesEditor.propTypes = {
  courseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  initialPrerequisites: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        description: PropTypes.string
      }),
      PropTypes.string
    ])
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
};

export default PrerequisitesEditor;